import { createEditionFormatter } from '../lib/editionFormatter';
import { AppEdition } from './appEdition';

export function isPhoneNumber(input: string): boolean {
    if (!input) return false;

    // Only digits and spaces allowed + an optional "+" at start
    const pattern = /^\+?[0-9 ]+$/;
    if (!pattern.test(input)) return false;

    // Count digits only (ignore spaces)
    const digitCount = input.replace(/\D/g, '').length;
    return digitCount >= 8 && digitCount <= 20;
}

export function isFullName(input: string): boolean {
    if (!input) return false;

    return input.trim().indexOf(' ') > -1;
}

/**
 * Validates standard email format (ASCII only) with:
 * - Local part allowing letters, digits, and special characters: . ! # $ % & ' * + / = ? ^ _ ` { | } ~ -
 * - No leading, trailing, or consecutive dots in the local part
 * - Domain part with letters, digits, hyphens, and subdomains
 * - TLD must be at least 2 letters
 * - Case-insensitive
 *
 * Note: Rejects non-ASCII characters (e.g., æ, ø, å, Unicode)
 */
export function isEmail(input: string): boolean {
    if (!input) return false;

    // common typos
    if (input.endsWith('@gmail.co')) return false;
    if (input.endsWith('@hotmail.co')) return false;
    if (input.endsWith('@outlook.co')) return false;
    if (input.endsWith('.con')) return false;
    if (input.endsWith('.clm')) return false;
    if (input.endsWith('.vom')) return false;

    // non-standard chars like "æ ø å" gets converted into punycode "xn--something" by the browser
    // this means the regez below won't catch it, so we need to explicitly reject it here
    if (input.includes('xn--')) return false;

    const emailRegex =
        /^(?!.*[^\x00-\x7F])(?!.*\.\.)(?!.*\.$)(?!^\.)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[A-Za-z]{2,}$/i;
    return emailRegex.test(input);
}

// This checks if the input looks like a Danish CPR number.
// It does not use the modulus validation, since not all CPR numbers use this anymore.
function isDanishCprNumber(input: string): boolean {
    if (!input) return false;

    if (!/^[0-9]{10}$/.test(input)) return false;

    const day = parseInt(input.substring(0, 2));
    const month = parseInt(input.substring(2, 4));
    const year = parseInt(input.substring(4, 6));
    const seventhDigit = parseInt(input.substring(6, 7));
    const century = getCprCentury(year, seventhDigit);
    const fullYear = century + year;

    const isValidMonth = 0 < month && month <= 12;

    // This gets us the number of days in the relevant month because:
    // - months are 0-based, so passing "month" will give us next month
    // passing day 0 will give us last day of previous month (the relevant month)
    const daysInMonth = new Date(fullYear, month, 0).getDate();
    const isValidDay = 0 < day && day <= daysInMonth;

    return isValidMonth && isValidDay;
}

// This checks if the input looks like a Norwegian national identity number (fødselsnummer).
// It does not use the modulus (control digit) validation, mirroring the Danish CPR check above.
function isNorwegianNationalIdentityNumber(input: string): boolean {
    if (!input) return false;

    if (!/^[0-9]{11}$/.test(input)) return false;

    let day = parseInt(input.substring(0, 2));
    // D-numbers (temporary identifiers, also used for fictitious test data) encode the day with +40
    if (day > 40) day -= 40;

    const month = parseInt(input.substring(2, 4));
    const year = parseInt(input.substring(4, 6));
    const individualNumber = parseInt(input.substring(6, 9));
    const century = getNorwegianCentury(year, individualNumber);
    const fullYear = century + year;

    const isValidMonth = 0 < month && month <= 12;

    const daysInMonth = new Date(fullYear, month, 0).getDate();
    const isValidDay = 0 < day && day <= daysInMonth;

    return isValidMonth && isValidDay;
}

export const isNationalIdentityNumber = createEditionFormatter({
    [AppEdition.Denmark]: isDanishCprNumber,
    [AppEdition.Norway]: isNorwegianNationalIdentityNumber,
});

export const isUnderCertainAge = (cpr: string, validate: number): boolean => {
    if (!cpr) return true;

    const cleanedCPR = cpr.replace(/[-\s]/gi, '');
    if (cleanedCPR.length < 7) return true;

    const birthday = nationalIdentifierToDateOfBirth(cleanedCPR);
    const maxAgeYearsAgo = new Date();
    maxAgeYearsAgo.setFullYear(maxAgeYearsAgo.getFullYear() - validate);
    return birthday > maxAgeYearsAgo;
};

const danishCprToDateOfBirth = (cpr: string): Date => {
    const day = parseInt(cpr.substring(0, 2));
    const month = parseInt(cpr.substring(2, 4));
    const year = parseInt(cpr.substring(4, 6));
    const seventhDigit = parseInt(cpr.substring(6, 7));
    const century = getCprCentury(year, seventhDigit);

    // months are zero indexed
    return new Date(century + year, month - 1, day);
};

const norwegianNationalIdentityNumberToDateOfBirth = (
    nationalIdentityNumber: string,
): Date => {
    let day = parseInt(nationalIdentityNumber.substring(0, 2));
    // D-numbers encode the day with +40.
    if (day > 40) day -= 40;

    const month = parseInt(nationalIdentityNumber.substring(2, 4));
    const year = parseInt(nationalIdentityNumber.substring(4, 6));
    const individualNumber = parseInt(nationalIdentityNumber.substring(6, 9));
    const century = getNorwegianCentury(year, individualNumber);

    return new Date(century + year, month - 1, day);
};

export const nationalIdentifierToDateOfBirth = createEditionFormatter({
    [AppEdition.Denmark]: danishCprToDateOfBirth,
    [AppEdition.Norway]: norwegianNationalIdentityNumberToDateOfBirth,
});

const getCprCentury = (year: number, seventhDigit: number): number => {
    switch (seventhDigit) {
        case 0:
        case 1:
        case 2:
        case 3:
            return 1900;
        case 4:
        case 9:
            if (year <= 36) {
                return 2000;
            } else {
                return 1900;
            }
        case 5:
        case 6:
        case 7:
        case 8:
            if (year <= 57) {
                return 2000;
            } else {
                return 1800;
            }
    }
};

// Based on the Norwegian individual number (siffer 7-9 of the fødselsnummer).
const getNorwegianCentury = (
    year: number,
    individualNumber: number,
): number => {
    if (individualNumber <= 499) {
        return 1900;
    }
    if (individualNumber >= 900 && year >= 40) {
        return 1900;
    }
    if (individualNumber <= 749 && year >= 54) {
        return 1800;
    }
    return 2000;
};
